import { type Handle, css } from 'remix/component'
import { prefetchRouteHrefs } from '#client/client-router.tsx'
import {
	type McpClientKind,
	type OnboardingAgentChooserPick,
	type OnboardingAgentSurface,
	type OnboardingAgentViewport,
	canonicalOnboardingAgentChooser,
	onboardingAgentHelp,
	onboardingAgentIconName,
	onboardingAgentLabel,
	onboardingAgentViewport,
	onboardingNotListedAgentIds,
	onboardingPickerAgentIds,
	onboardingViewportCss,
} from '#client/routes/onboarding-mcp-clients.ts'
import { renderIcon } from '#universal/icon.tsx'
import {
	type OnboardingSecondAgentDisableReason,
	type OnboardingStep3EcosystemGroup,
	onboardingSecondAgentDisableHint,
} from '#universal/onboarding-agent-ecosystems.ts'
import { onboardingAgentHref } from '#universal/onboarding-process.ts'
import {
	colors,
	radius,
	transitions,
	typography,
} from '#universal/styles/tokens.ts'
import { hoverMq } from '#universal/styles/style-primitives.ts'
import { type HighlightedCode } from '#universal/highlighted-code.ts'
import { AgentAuthCallout } from './onboarding-agent-auth-callout.tsx'
import {
	renderPanelContent,
	renderPanelWarning,
} from './onboarding-mcp-client-panels.tsx'
import { onboardingAgentPickerPrefetchHrefs } from './onboarding-picker-prefetch.ts'

type OnboardingMcpClientTabsProps = {
	mcpServerUrl: string
	highlights?: Record<string, HighlightedCode>
	selectedAgent?: McpClientKind | null
	chooser?: OnboardingAgentChooserPick | null
	search?: string
	agentHref?: (agent: McpClientKind | null, search?: string) => string
	pickerLede?: string
	greyedAgents?: ReadonlyArray<McpClientKind>
	greyedReasons?: Partial<
		Record<McpClientKind, OnboardingSecondAgentDisableReason>
	>
	greyedTitles?: Partial<Record<McpClientKind, string>>
	greyedReason?: string | null
	ecosystemGroups?: ReadonlyArray<OnboardingStep3EcosystemGroup>
}

function AgentMarkIcon(
	handle: Handle<{ icon: string | null; size?: 'picker' | 'inline' }>,
) {
	return () => {
		const inline = handle.props.size === 'inline'
		if (!handle.props.icon) {
			return renderIcon('dots-horizontal', {
				size: inline ? '1cap' : '22',
			})
		}
		return (
			<img
				src={`/images/icons/${handle.props.icon}.svg`}
				alt=""
				width={inline ? undefined : 28}
				height={inline ? undefined : 28}
				mix={css(inline ? pickerIconImgInlineCss : pickerIconImgCss)}
			/>
		)
	}
}

export function AgentPickerMark(
	handle: Handle<{
		agent: McpClientKind
		testId?: string
		size?: 'picker' | 'inline'
	}>,
) {
	return () => {
		const desktopIcon = onboardingAgentIconName(handle.props.agent, 'desktop')
		const mobileIcon = onboardingAgentIconName(handle.props.agent, 'mobile')
		const size = handle.props.size ?? 'picker'
		return (
			<span
				mix={css(size === 'inline' ? pickerMarkInlineCss : pickerMarkCss)}
				aria-hidden="true"
				data-testid={handle.props.testId}
				data-mark-size={size}
			>
				{desktopIcon === mobileIcon ? (
					<AgentMarkIcon icon={desktopIcon} size={size} />
				) : (
					<>
						<span
							mix={css(
								onboardingViewportCss(
									'desktop-only',
									size === 'inline' ? 'inline-block' : 'grid',
								),
							)}
						>
							<AgentMarkIcon icon={desktopIcon} size={size} />
						</span>
						<span
							mix={css(
								onboardingViewportCss(
									'mobile-only',
									size === 'inline' ? 'inline-block' : 'grid',
								),
							)}
						>
							<AgentMarkIcon icon={mobileIcon} size={size} />
						</span>
					</>
				)}
			</span>
		)
	}
}

function AgentSurfaceLabel(handle: Handle<{ agent: McpClientKind }>) {
	return () => {
		const desktop = onboardingAgentLabel(handle.props.agent, 'desktop')
		const mobile = onboardingAgentLabel(handle.props.agent, 'mobile')
		if (desktop === mobile) return desktop
		return (
			<>
				<span mix={css(onboardingViewportCss('desktop-only', 'inline'))}>
					{desktop}
				</span>
				<span mix={css(onboardingViewportCss('mobile-only', 'inline'))}>
					{mobile}
				</span>
			</>
		)
	}
}

function AgentHelpLink(
	handle: Handle<{
		agent: McpClientKind
		surface: OnboardingAgentSurface
	}>,
) {
	return () => {
		const help = onboardingAgentHelp(handle.props.agent, handle.props.surface)
		return (
			<p mix={css(agentHelpCss)}>
				Need help?{' '}
				<a
					href={help.href}
					target="_blank"
					rel="noreferrer noopener"
					data-testid="onboarding-agent-help"
				>
					{help.label}
				</a>
			</p>
		)
	}
}

/**
 * Step 1: pick one agent, then show only that host's install path and
 * authenticate hint. Featured hosts are the first chooser; other named
 * hosts are under More; **Not listed** is the generic MCP URL path.
 * Step 3 passes `ecosystemGroups` and renders those groups instead.
 */
export function OnboardingMcpClientTabs(
	handle: Handle<OnboardingMcpClientTabsProps>,
) {
	let warmedKey = ''
	return () => {
		const { mcpServerUrl, highlights } = handle.props
		const selectedAgent = handle.props.selectedAgent ?? null
		const chooser = handle.props.chooser ?? canonicalOnboardingAgentChooser()
		const search = handle.props.search ?? ''
		const agentHref = handle.props.agentHref ?? onboardingAgentHref
		const greyedAgents = handle.props.greyedAgents ?? []
		const greyedReasons = handle.props.greyedReasons ?? {}
		const greyedTitles = handle.props.greyedTitles ?? {}
		const ecosystemGroups = handle.props.ecosystemGroups
		const prefetchAgentIds = ecosystemGroups?.flatMap((group) => [
			...group.agents,
		])
		handle.queueTask(() => {
			const hrefs = onboardingAgentPickerPrefetchHrefs(
				selectedAgent,
				chooser,
				search,
				agentHref,
				prefetchAgentIds,
			)
			const key = hrefs.join('\0')
			if (key === warmedKey) return
			warmedKey = key
			prefetchRouteHrefs(hrefs)
		})

		if (!selectedAgent && ecosystemGroups && ecosystemGroups.length > 0) {
			return (
				<div
					data-testid="onboarding-agent-picker"
					data-picker="ecosystem"
					mix={css(installLayoutCss)}
				>
					<p mix={css(pickerLedeCss)} id="onboarding-agent-picker-label">
						{handle.props.pickerLede ??
							'Choose the agent you want to connect first. You can add others later.'}
					</p>
					<div
						data-testid="onboarding-ecosystem-picker"
						mix={css(ecosystemPickerCss)}
					>
						{ecosystemGroups.map((group) => {
							const labelId = `onboarding-ecosystem-${group.id}-label`
							return (
								<section
									key={group.id}
									data-testid={`onboarding-ecosystem-${group.id}`}
									aria-labelledby={labelId}
									mix={css(ecosystemGroupCss)}
								>
									<h3 id={labelId} mix={css(ecosystemLabelCss)}>
										{group.label}
									</h3>
									<AgentPickerGrid
										ids={group.agents.map((id) => ({
											id,
											viewport: 'both' as const,
										}))}
										labelledBy={labelId}
										search={search}
										agentHref={agentHref}
										greyedAgents={greyedAgents}
										greyedReasons={greyedReasons}
										greyedTitles={greyedTitles}
										greyedReason={handle.props.greyedReason ?? null}
									/>
								</section>
							)
						})}
					</div>
				</div>
			)
		}

		if (!selectedAgent) {
			return (
				<div data-testid="onboarding-agent-picker" mix={css(installLayoutCss)}>
					<p mix={css(pickerLedeCss)} id="onboarding-agent-picker-label">
						{handle.props.pickerLede ??
							'Choose the agent you want to connect first. You can add others later.'}
					</p>
					<AgentPickerGrid
						ids={[...onboardingPickerAgentIds(chooser), 'other']}
						labelledBy="onboarding-agent-picker-label"
						search={search}
						agentHref={agentHref}
						greyedAgents={greyedAgents}
						greyedReasons={greyedReasons}
						greyedTitles={greyedTitles}
						greyedReason={handle.props.greyedReason ?? null}
					/>
				</div>
			)
		}

		if (selectedAgent === 'other') {
			return (
				<div
					data-testid="onboarding-agent-not-listed"
					mix={css(installLayoutCss)}
				>
					<p mix={css(pickerLedeCss)} id="onboarding-agent-not-listed-label">
						Any of these what you're looking for?
					</p>
					<AgentPickerGrid
						ids={onboardingNotListedAgentIds(chooser)}
						labelledBy="onboarding-agent-not-listed-label"
						search={search}
						agentHref={agentHref}
						greyedAgents={greyedAgents}
						greyedReasons={greyedReasons}
						greyedTitles={greyedTitles}
						greyedReason={handle.props.greyedReason ?? null}
					/>
					<p mix={css(pickerLedeCss)} id="onboarding-agent-not-listed-generic">
						Or, connect any agent that speaks MCP
					</p>
					<div
						data-testid="onboarding-agent-instructions"
						data-agent="other"
						mix={css(installLayoutCss)}
					>
						<AgentSurfaceInstructions
							agent="other"
							mcpServerUrl={mcpServerUrl}
							highlights={highlights}
						/>
					</div>
				</div>
			)
		}

		return (
			<div
				data-testid="onboarding-agent-instructions"
				data-agent={selectedAgent}
				mix={css(installLayoutCss)}
			>
				<AgentSurfaceInstructions
					agent={selectedAgent}
					mcpServerUrl={mcpServerUrl}
					highlights={highlights}
				/>
			</div>
		)
	}
}

/**
 * One host's install path: desktop and phone surfaces both render and CSS
 * shows the one that matches the viewport, followed by the help link, any
 * host caveat, and the authenticate callout. Shared with Add connection on
 * `/account/connections/new`.
 */
export function AgentSurfaceInstructions(
	handle: Handle<{
		agent: McpClientKind
		mcpServerUrl: string
		highlights?: Record<string, HighlightedCode>
	}>,
) {
	return () => (
		<>
			<div
				data-surface="desktop"
				mix={[
					css(onboardingViewportCss('desktop-only', 'grid')),
					css(agentSurfaceStackCss),
				]}
			>
				<div mix={css(selectedPanelCss)}>
					{renderPanelContent(
						handle.props.agent,
						handle.props.mcpServerUrl,
						handle.props.highlights,
						'desktop',
					)}
					<AgentHelpLink agent={handle.props.agent} surface="desktop" />
					{renderPanelWarning(handle.props.agent, 'desktop')}
				</div>
				<AgentAuthCallout
					agent={handle.props.agent}
					surface="desktop"
					mcpServerUrl={handle.props.mcpServerUrl}
				/>
			</div>
			<div
				data-surface="mobile"
				mix={[
					css(onboardingViewportCss('mobile-only', 'grid')),
					css(agentSurfaceStackCss),
				]}
			>
				<div mix={css(selectedPanelCss)}>
					{renderPanelContent(
						handle.props.agent,
						handle.props.mcpServerUrl,
						handle.props.highlights,
						'mobile',
					)}
					<AgentHelpLink agent={handle.props.agent} surface="mobile" />
					{renderPanelWarning(handle.props.agent, 'mobile')}
				</div>
				<AgentAuthCallout
					agent={handle.props.agent}
					surface="mobile"
					mcpServerUrl={handle.props.mcpServerUrl}
				/>
			</div>
		</>
	)
}

/**
 * The client wall. Entries given as bare ids follow the onboarding
 * viewport split (desktop-only hosts hide on a phone); pass
 * `{ id, viewport: 'both' }` entries to show every card everywhere, which
 * is what Add connection on `/account/connections/new` does.
 *
 * `greyedAgents` marks hosts Kody already knows are connected. Those cards
 * stay selectable so someone can re-view connect steps (second login, new
 * machine, reinstall) — Connected is a badge, not a dead end.
 */
export function AgentPickerGrid(
	handle: Handle<{
		ids:
			| Array<McpClientKind>
			| Array<{ id: McpClientKind; viewport: OnboardingAgentViewport }>
		labelledBy: string
		search?: string
		agentHref: (agent: McpClientKind | null, search?: string) => string
		greyedAgents?: ReadonlyArray<McpClientKind>
		greyedReasons?: Partial<
			Record<McpClientKind, OnboardingSecondAgentDisableReason>
		>
		greyedTitles?: Partial<Record<McpClientKind, string>>
		greyedReason?: string | null
	}>,
) {
	return () => (
		<ul aria-labelledby={handle.props.labelledBy} mix={css(pickerGridCss)}>
			{handle.props.ids.map((entry) => {
				const id = typeof entry === 'string' ? entry : entry.id
				const viewport =
					typeof entry === 'string'
						? onboardingAgentViewport(id)
						: entry.viewport
				const shown = viewport === 'none' ? 'both' : viewport
				const greyedAgents = handle.props.greyedAgents ?? []
				const greyedReasons = handle.props.greyedReasons ?? {}
				const greyedTitles = handle.props.greyedTitles ?? {}
				const search = handle.props.search ?? ''
				const connectedMark = greyedAgents.includes(id)
				const reason = greyedReasons[id] ?? 'connected'
				const title = greyedTitles[id] ?? handle.props.greyedReason ?? null
				return (
					<li key={id} mix={css(onboardingViewportCss(shown, 'list-item'))}>
						<a
							href={handle.props.agentHref(id, search)}
							data-testid={`onboarding-agent-${id}`}
							data-prevent-scroll-reset=""
							data-greyed={connectedMark ? 'true' : undefined}
							data-greyed-reason={connectedMark ? reason : undefined}
							title={connectedMark ? (title ?? undefined) : undefined}
							mix={css(
								connectedMark ? pickerCardConnectedMarkCss : pickerCardCss,
							)}
						>
							<AgentPickerMark agent={id} />
							<strong>
								<AgentSurfaceLabel agent={id} />
							</strong>
							{connectedMark ? (
								<span mix={css(connectedMarkHintCss)}>
									{onboardingSecondAgentDisableHint(reason)}
								</span>
							) : null}
						</a>
					</li>
				)
			})}
		</ul>
	)
}

const installLayoutCss = {
	display: 'grid',
	gap: '1.15rem',
}

/* Warning and authenticate callouts are siblings of the instruction
   stack. Without a gap they sit flush and read as one block. */
const agentSurfaceStackCss = {
	gap: '1rem',
	alignContent: 'start' as const,
}

const pickerLedeCss = {
	margin: 0,
	color: colors.textMuted,
	maxWidth: '72ch',
}

const ecosystemPickerCss = {
	display: 'grid',
	gap: '1.25rem',
}

const ecosystemGroupCss = {
	display: 'grid',
	gap: '0.5rem',
}

const ecosystemLabelCss = {
	margin: 0,
	color: colors.textMuted,
	fontSize: typography.fontSize.sm,
	fontWeight: typography.fontWeight.semibold,
	letterSpacing: '0.04em',
	textTransform: 'uppercase' as const,
}

const pickerGridCss = {
	listStyle: 'none',
	margin: 0,
	padding: 0,
	display: 'grid',
	alignItems: 'stretch',
	gridTemplateColumns: 'repeat(auto-fill, minmax(min(10.5rem, 100%), 1fr))',
	gap: '0.75rem',
}

const pickerCardCss = {
	display: 'grid',
	justifyItems: 'center',
	alignContent: 'center',
	gap: '0.55rem',
	width: '100%',
	height: '100%',
	minWidth: 0,
	padding: '1.05rem 0.85rem',
	backgroundColor: colors.background,
	border: `1.5px solid ${colors.border}`,
	borderRadius: radius.card,
	color: colors.text,
	cursor: 'pointer',
	textDecoration: 'none',
	boxSizing: 'border-box' as const,
	textAlign: 'center' as const,
	font: `650 0.98rem/1.25 ${typography.fontFamilyBody}`,
	transition: `border-color 160ms ${transitions.easeOut}, transform 160ms ${transitions.easeOut}`,
	[hoverMq]: {
		'&:hover': {
			borderColor: colors.primary,
			transform: 'translateY(-2px)',
		},
	},
	'&:active': { transform: 'translateY(0)' },
	'@media (prefers-reduced-motion: reduce)': {
		transition: `border-color 160ms ${transitions.easeOut}`,
		'&:hover': { transform: 'none' },
		'&:active': { transform: 'none' },
	},
}

const pickerCardConnectedMarkCss = {
	...pickerCardCss,
	borderStyle: 'dashed' as const,
}

const connectedMarkHintCss = {
	font: `550 0.72rem/1 ${typography.fontFamilyBody}`,
	letterSpacing: '0.04em',
	textTransform: 'uppercase' as const,
	color: colors.textMuted,
}

const pickerMarkCss = {
	display: 'grid',
	placeItems: 'center',
	flex: 'none',
	width: '1.75rem',
	height: '1.75rem',
	color: colors.text,
}

const pickerMarkInlineCss = {
	display: 'inline-block',
	width: '1cap',
	height: '1cap',
	overflow: 'hidden',
	verticalAlign: 'baseline',
	marginInlineEnd: '0.25em',
	color: colors.text,
}

const pickerIconImgCss = {
	display: 'block',
	width: '1.75rem',
	height: '1.75rem',
	objectFit: 'contain' as const,
	'@media (prefers-color-scheme: dark)': {
		filter: 'invert(1)',
	},
}

const pickerIconImgInlineCss = {
	display: 'block',
	width: '1cap',
	height: '1cap',
	objectFit: 'contain' as const,
	'@media (prefers-color-scheme: dark)': {
		filter: 'invert(1)',
	},
}

const selectedPanelCss = {
	display: 'grid',
	gap: '0.9rem',
	minWidth: 0,
	color: colors.text,
	'@media (prefers-reduced-motion: no-preference)': {
		transition: `opacity 240ms ${transitions.easeOut}, translate 240ms ${transitions.easeOut}`,
	},
	'@starting-style': {
		opacity: 0,
		translate: '0 6px',
	},
	'& > p': {
		margin: 0,
		color: colors.textMuted,
		maxWidth: '72ch',
	},
	'& > p a': {
		color: colors.primaryText,
	},
	'& code': {
		font: '500 0.88em ui-monospace, "SF Mono", Menlo, monospace',
		color: colors.text,
		backgroundColor: colors.background,
		border: `1px solid ${colors.border}`,
		borderRadius: '6px',
		padding: '0.1em 0.4em',
	},
	'& pre code': {
		font: 'inherit',
		color: 'inherit',
		backgroundColor: 'transparent',
		border: 'none',
		borderRadius: 0,
		padding: 0,
	},
}

const agentHelpCss = {
	// Beat `selectedPanelCss` `& > p { margin: 0 }` so this sits off the
	// authenticate banner.
	'&&': {
		margin: '0 0 1rem',
	},
	fontSize: typography.fontSize.sm,
}
